import {
  Button,
  Input,
  Select,
  SelectItem,
  Spinner,
  Tooltip,
  Switch,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@nextui-org/react";
import { useEffect, useRef, useState } from "react";
import { useProblem } from "../../contexts/problem-context";
import fetchWrapper from "../../utils/fetch";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useAuth, decodeJWT } from "../../contexts/auth-context";
import Slider from "react-input-slider";
import { motion, AnimatePresence } from "framer-motion";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Accordion } from "@szhsin/react-accordion";
import { AccordionItem } from "../../components/accordion/accordion";
import { StrictModeDroppable } from "../../components/strict-mode-droppable";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { isEqual } from "lodash";
import { toast } from "react-toastify";

const ItemTypes = {
  CRITERIA_OPTION: "criteriaOption",
};

const CriteriaOption = ({
  id,
  rate,
  index,
  moveOption,
  rateIndex,
  editingRate,
  handleRateEdit,
  setEditingRate,
  removeRate,
  criteriaKey,
}) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop({
    accept: ItemTypes.CRITERIA_OPTION,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveOption(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CRITERIA_OPTION,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <motion.div
      ref={ref}
      style={{ opacity }}
      className={`flex items-center justify-between bg-white rounded-lg px-4 py-3 shadow-sm border 
        ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-200"} 
        hover:border-blue-500 transition-all cursor-move`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      whileHover={{ scale: 1.02 }}
      data-handler-id={handlerId}
    >
      <div className="flex items-center gap-3">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className={`w-5 h-5 ${isDragging ? "text-blue-500" : "text-gray-400"}`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
          />
        </svg>
        {editingRate === `${criteriaKey}-${rateIndex}` ? (
          <div className="flex items-center gap-2">
            <Input
              type="text"
              defaultValue={rate}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRateEdit(criteriaKey, rateIndex, e.target.value);
                } else if (e.key === "Escape") {
                  setEditingRate(null);
                }
              }}
              onBlur={(e) => {
                if (e.target.value.trim() && e.target.value.trim() !== rate) {
                  handleRateEdit(criteriaKey, rateIndex, e.target.value);
                } else {
                  setEditingRate(null);
                }
              }}
              className="w-32"
            />
            <Button
              size="sm"
              variant="light"
              onPress={() => setEditingRate(null)}
              className="text-gray-500"
            >
              لغو
            </Button>
          </div>
        ) : (
          <span>{rate}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="light"
          onPress={() => setEditingRate(`${criteriaKey}-${rateIndex}`)}
          className="text-gray-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
            />
          </svg>
        </Button>
        <Button
          size="sm"
          variant="light"
          color="danger"
          onPress={() => removeRate(criteriaKey, rateIndex)}
          className="text-gray-500"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            />
          </svg>
        </Button>
      </div>
    </motion.div>
  );
};

export default function CreateProblem() {
  const nameInputRef = useRef(null);
  const tokenRequestInProgressRef = useRef(false);
  const [{ problemType, weights: contextWeights }, problemDispatch] = useProblem();
  const [searchParams] = useSearchParams();
  const template_id = searchParams.get("template_id");
  const [category, setCategory] = useState("private");
  const [photoUrl, setPhotoUrl] = useState("");
  const [save, setSave] = useState(false);
  const [
    { user, isAuthenticated, requested, hasTemporaryToken },
    authDispatch,
    { canAccessProtectedFeatures },
  ] = useAuth();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isForCrawler, setIsForCrawler] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [weights, setWeights] = useState([2, 2]);
  const [numericalInputs, setNumericalInputs] = useState([]);
  const [positivities, setPositivities] = useState([true, true]);

  const [editingName, setEditingName] = useState(null);
  const [editingRate, setEditingRate] = useState(null);

  const [varriables, setVarriables] = useState({
    "معیار شماره‌ی 1": ["خوب", "متوسط", "بد"],
    "معیار شماره‌ی 2": ["خوب", "متوسط", "بد"],
  });
  const [dirtyVarriables, setDirtyVarriables] = useState(false);

  

  useEffect(() => {
    if (template_id) {
      setIsLoading(true);
      if (tokenRequestInProgressRef.current) return;
      const accessToken = localStorage.getItem("access_token");
      let tokenExpired = false;
      if (accessToken) {
        try {
          const tokenData = decodeJWT(accessToken);
          if (!tokenData || !tokenData.exp) {
            console.log("Invalid token");
            tokenExpired = true;
            localStorage.removeItem("access_token");
          } else {
            const expirationTime = tokenData.exp * 1000;
            if (Date.now() >= expirationTime) {
              console.log("Token expired");
              tokenExpired = true;
              localStorage.removeItem("access_token");
            }
          }
        } catch (error) {
          tokenExpired = true;
          localStorage.removeItem("access_token");
        }
      }
      if ((!accessToken || tokenExpired) && !isAuthenticated && !hasTemporaryToken) {
        tokenRequestInProgressRef.current = true;
        const setTokenAndFetch = async () => {
          try {
            const data = await fetchWrapper.post("/get/temporary-token");
            localStorage.setItem("access_token", data.access_token);
            authDispatch({ type: "set temporary token", payload: data.access_token });
            await new Promise((resolve) => setTimeout(resolve, 50));
            const storedToken = localStorage.getItem("access_token");
            if (!storedToken) throw new Error("Failed to set token in localStorage");
            await fetchProblemData(template_id);
          } catch (err) {
            if (err.detail === "not found") {
              toast.error("این تمپلیت موجود نیست");
            } else if (err.detail === "Invalid credentials" || err.detail === "private problem") {
              toast.error("برای استفاده باید وارد شوید.");
              navigate("/login");
            } else {
              toast.error(err.message || "در دریافت توکن موقت مشکلی پیش آمده");
              navigate("/");
            }
          } finally {
            tokenRequestInProgressRef.current = false;
            setIsLoading(false);
          }
        };
        setTokenAndFetch();
      } else {
        fetchProblemData(template_id)
          .catch((err) => {
            if (err.detail === "not found") {
              toast.error("این تمپلیت موجود نیست");
              navigate("/");
            } else if (err.detail === "Invalid credentials" || err.detail === "private problem") {
              if (!isAuthenticated && !tokenRequestInProgressRef.current) {
                tokenRequestInProgressRef.current = true;
                const getTemporaryTokenAndRetry = async () => {
                  try {
                    const data = await fetchWrapper.post("/get/temporary-token");
                    localStorage.setItem("access_token", data.access_token);
                    authDispatch({ type: "set temporary token", payload: data.access_token });
                    await new Promise((resolve) => setTimeout(resolve, 50));
                    const storedToken = localStorage.getItem("access_token");
                    if (!storedToken) throw new Error("Failed to set token in localStorage");
                    await fetchProblemData(template_id);
                  } catch (retryErr) {
                    toast.error("برای استفاده باید وارد شوید.");
                    navigate("/login");
                  } finally {
                    tokenRequestInProgressRef.current = false;
                    setIsLoading(false);
                  }
                };
                getTemporaryTokenAndRetry();
                return;
              } else {
                toast.error("برای استفاده باید وارد شوید.");
                navigate("/login");
              }
            } else {
              toast.error(err.message || "خطایی رخ داد");
              navigate("/");
            }
          })
          .finally(() => setIsLoading(false));
      }
    } else {
      let ws = [2, 2];
      let pss = [true, true];
      if (contextWeights && contextWeights.length > 0) {
        ws = [];
        pss = [];
        contextWeights.forEach((w) => {
          ws.push(w[0]);
          pss.push(w[1]);
        });
      }
      setWeights(ws);
      setPositivities(pss);
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [searchParams, problemDispatch, navigate, contextWeights, isAuthenticated, hasTemporaryToken]);

  // Helper function to fetch problem data
  const fetchProblemData = (template_id) => {
    return fetchWrapper
      .post("/get/problem", { problem_uid: template_id })
      .then((data) => {
        const vs = {};
        const ws = [];
        const ni = [];
        const ps = [];
        data.criteria_names.forEach((varriableKey, varriableI) => {
          vs[varriableKey] = data.rate_names[varriableI];
          if (data.problem_type === "saw") {
            ws.push(Math.floor(data.weights[varriableI][0]));
            ni.push(data.weights[varriableI][2]);
            ps.push(data.weights[varriableI][1] ? true : false);
          }
        });
        problemDispatch({
          type: "load problem",
          payload: {
            alternatives: data.alternatives,
            criteria_rates: data.criteria_rates,
            problemType: data.problem_type,
            template_id: template_id,
            copy_answers: true,
            varriables: vs,
          },
        });
        setWeights(ws);
        setPositivities(ps);
        setNumericalInputs(ni);
        setVarriables(vs);
        setIsForCrawler(data.is_for_crawler);
        setIsInitialized(true);
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problemName = nameInputRef.current?.value || "";
    if (save && !problemName.trim()) {
      toast.error("وارد کردن نام تصمیم اجباری ست.");
      return;
    }
    if (Object.keys(varriables).length === 0) {
      toast.error("حداقل یک معیار باید اضافه شود.");
      return;
    }
    setIsLoading(true);
    let cr = [];
    const crn = [];
    const rn = [];
    let ws = [];
    Object.entries(varriables).forEach(([key, rates]) => {
      cr.push(rates.length);
      crn.push(key);
      rn.push(rates);
    });
    if (problemType === "saw") {
      cr = [];
      weights.forEach((w, index) => {
        if (w === undefined || w === null) {
          console.error(`Invalid weight at index ${index}`);
          return;
        }
        ws.push([w, positivities[index] ?? true, numericalInputs[index] ?? false]);
      });
    }
    try {
      const data = await fetchWrapper.post("/create/problem", {
        criteria_rates: cr,
        criteria_names: crn,
        rate_names: rn,
        problem_name: problemName,
        weights: ws,
        category: category,
        photo: photoUrl,
        problem_type: problemType,
        saved: save,
        is_for_crawler: isForCrawler,
      });
      problemDispatch({
        type: "create problem",
        payload: {
          varriables: varriables,
          problemName: problemName,
          uid: data.problem_uid,
          question_pairs: [data.question_pairs],
          criteria_rates: cr,
          category: category,
          photo: photoUrl,
          weights: ws,
          dirty_varriables: dirtyVarriables,
        },
      });
      setIsLoading(false);
      navigate("/problem/alternatives");
    } catch (err) {
      console.error("Error creating problem:", err);
      toast.error(err?.detail || "خطا در ایجاد تصمیم");
      setIsLoading(false);
    }
  };

  const uploadPhoto = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const fd = new FormData();
    fd.append("photo", e.target.files[0]);
    fetchWrapper
      .post("/upload-pic", fd, true)
      .then((data) => {
        setPhotoUrl(data.photo_url);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const changeWeights = (index, value) => {
    const w = [...weights];
    w[index] = value;
    setWeights(w);
  };

  const changePositivities = (index, value) => {
    const p = [...positivities];
    p[index] = !value;
    setPositivities(p);
  };

  // Move an option within a criteria list
  const moveOption = (dragIndex, hoverIndex, criteriaKey) => {
    const newItems = [...varriables[criteriaKey]];
    const [draggedItem] = newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, draggedItem);
    setDirtyVarriables(true);
    setVarriables((prev) => ({
      ...prev,
      [criteriaKey]: newItems,
    }));
  };

  // Add a new rate to a criteria
  const addRate = (e, criteriaKey) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const rateName = formData.get("rate_name");
    const currentRates = varriables[criteriaKey];
    if (currentRates.includes(rateName)) {
      toast.error("اجازه‌ی اضافه کردن رده‌ی تکراری ندارد");
      return;
    }
    const updatedRates = [...currentRates, rateName];
    setDirtyVarriables(true);
    setVarriables((prev) => ({
      ...prev,
      [criteriaKey]: updatedRates,
    }));
    e.target.reset();
  };

  // Remove a rate from a criteria
  const removeRate = (criteriaKey, rateIndex) => {
    const currentRates = [...varriables[criteriaKey]];
    if (currentRates.length === 1) {
      toast.error("حداقل یک حالت الزامی‌ست.");
      return;
    }
    currentRates.splice(rateIndex, 1);
    setDirtyVarriables(true);
    setVarriables((prev) => ({
      ...prev,
      [criteriaKey]: currentRates,
    }));
  };

  // Move a rate up in the list
  const moveUp = (criteriaKey, rateIndex) => {
    if (rateIndex === 0) {
      toast.warning("این معیار در بالاترین موقعیت قرار دارد");
      return;
    }
    const currentRates = [...varriables[criteriaKey]];
    const temp = currentRates[rateIndex - 1];
    currentRates[rateIndex - 1] = currentRates[rateIndex];
    currentRates[rateIndex] = temp;
    setDirtyVarriables(true);
    setVarriables((prev) => ({
      ...prev,
      [criteriaKey]: currentRates,
    }));
  };

  // Move a rate down in the list
  const moveDown = (criteriaKey, rateIndex) => {
    const currentRates = [...varriables[criteriaKey]];
    if (rateIndex === currentRates.length - 1) {
      toast.warning("این معیار در پایین‌ترین موقعیت قرار دارد");
      return;
    }
    const temp = currentRates[rateIndex + 1];
    currentRates[rateIndex + 1] = currentRates[rateIndex];
    currentRates[rateIndex] = temp;
    setDirtyVarriables(true);
    setVarriables((prev) => ({
      ...prev,
      [criteriaKey]: currentRates,
    }));
  };

  // Edit a criteria name
  const handleNameEdit = (oldName, newName) => {
    if (!newName.trim() || newName.trim() === oldName) {
      setEditingName(null);
      return;
    }
    const existingNames = Object.keys(varriables);
    if (existingNames.includes(newName.trim()) && newName.trim() !== oldName) {
      toast.error("این نام معیار قبلاً استفاده شده است. لطفاً نام دیگری انتخاب کنید.");
      return;
    }
    const updated = {};
    Object.entries(varriables).forEach(([key, value]) => {
      if (key === oldName) {
        updated[newName.trim()] = value;
      } else {
        updated[key] = value;
      }
    });
    setDirtyVarriables(true);
    setVarriables(updated);
    setEditingName(null);
  };

  // Edit a rate value
  const handleRateEdit = (criteriaKey, rateIndex, newRate) => {
    if (!newRate.trim() || newRate.trim() === varriables[criteriaKey][rateIndex]) {
      setEditingRate(null);
      return;
    }
    const updatedRates = [...varriables[criteriaKey]];
    if (updatedRates.some((rate, idx) => rate === newRate.trim() && idx !== rateIndex)) {
      toast.error("اجازه‌ی اضافه کردن رده‌ی تکراری ندارد");
      return;
    }
    updatedRates[rateIndex] = newRate.trim();
    setDirtyVarriables(true);
    setVarriables((prev) => ({
      ...prev,
      [criteriaKey]: updatedRates,
    }));
    setEditingRate(null);
  };

  const addVarriable = (e, varriableName) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const rateName = formData.get("rate_name");

    const varriableRates = varriables[varriableName];
    if (varriableRates.includes(rateName)) {
      toast.error("اجازه‌ی اضافه کردن رده‌ی تکراری ندارد");
      return;
    }
    varriableRates.push(rateName);
    setDirtyVarriables(true);
    setVarriables((v) => {
      return {
        ...v,
        [varriableName]: varriableRates,
      };
    });
    e.target.reset();
  };
  const removeVarriable = (varriableName, index) => {
    const vs = {
      ...varriables,
    };
    const nw = [...weights];
    const np = [...positivities];
    nw.splice(index, 1);
    np.splice(index, 1);
    setWeights(nw);
    setPositivities(np);
    delete vs[varriableName];
    setDirtyVarriables(true);
    setVarriables(vs);
  };

  if (!requested) {
    return <Spinner size="lg" />;
  }

  if (!isAuthenticated && !hasTemporaryToken && !template_id) {
    return <Navigate to="/login" />;
  }

  if (isLoading || !isInitialized) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <h2 className="text-2xl font-bold text-center">مرحله ایجاد تصمیم</h2>
          <p className="mt-4 text-center">در این مرحله می توانید تصمیم خود را ایجاد کنید</p>

          <div className="mt-12">
            <div className="mb-6">
                  <Input
                    ref={nameInputRef}
                    type="string"
                    label="نام تصمیم"
                    name="problem_name"
                    placeholder="مثلا انتخاب بهترین ماشین از بین ماشین‌های ژاپنی  ..."
                    labelPlacement="outside"
                    isRequired={true}
                  />
                </div>

            <div className="criteria-section">
                    <div className="flex flex-row items-center mt-2 gap-2">
                      <h3 className="text-lg font-bold">معیار‌ها</h3>
                      <Tooltip
                        content="شاخص‌هایی که برا اساس آنها تصمیم‌گیری می‌شود."
                        showArrow={true}
                        color="primary"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                          />
                        </svg>
                      </Tooltip>
                    </div>

                    <div className="my-4 font-bold">
                {problemType === "normal" ? (
                  <Accordion transition transitionTimeout={250}>
                    <AnimatePresence>
                      {Object.keys(varriables).map((key, index) => (
                        <motion.div
                          key={key}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{
                            opacity: [1, 0],
                            scale: [1, 1.2, 0],
                            rotate: [0, 5, -5, 0],
                            transition: {
                              duration: 0.2,
                              times: [0, 0.2, 1],
                              ease: "easeOut",
                            },
                          }}
                          transition={{ duration: 0.4 }}
                        >
                          <AccordionItem
                            key={index}
                            header={
                              <div className="flex items-center gap-2">
                                {editingName === key ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="text"
                                      defaultValue={key}
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleNameEdit(key, e.target.value);
                                        } else if (e.key === "Escape") {
                                          setEditingName(null);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        if (e.target.value.trim() && e.target.value.trim() !== key) {
                                          handleNameEdit(key, e.target.value);
                                        } else {
                                          setEditingName(null);
                                        }
                                      }}
                                      className="w-48"
                                    />
                                    <Button
                                      size="sm"
                                      variant="light"
                                      onPress={() => setEditingName(null)}
                                      className="text-gray-500"
                                    >
                                      لغو
                                      </Button>
                                    </div>
                                ) : (
                                  <>
                                    <span>{key}</span>
                                    <Button
                                      size="sm"
                                      variant="light"
                                      onPress={() => setEditingName(key)}
                                      className="text-gray-500"
                                    >
                                      <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-5 h-5"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                                    />
                                  </svg>
                                </Button>
                              </>
                            )}
                          </div>
                        }
                      >
                        <div className="space-y-4">
                          <div className="relative bg-gray-50 rounded-lg p-4">
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                              بهترین
                            </div>
                            <div className="space-y-2 min-h-[200px] transition-colors">
                              {varriables[key].map((rate, idx) => (
                                <CriteriaOption
                                  key={rate}
                                  id={`${key}-${rate}`}
                                  rate={rate}
                                  index={idx}
                                  moveOption={(dragIndex, hoverIndex) => {
                                    const newItems = [...varriables[key]];
                                    const [draggedItem] = newItems.splice(dragIndex, 1);
                                    newItems.splice(hoverIndex, 0, draggedItem);
                                    setDirtyVarriables(true);
                                    setVarriables((prev) => ({
                                      ...prev,
                                      [key]: newItems,
                                    }));
                                  }}
                                  rateIndex={idx}
                                  editingRate={editingRate}
                                  handleRateEdit={handleRateEdit}
                                  setEditingRate={setEditingRate}
                                  removeRate={removeRate}
                                  criteriaKey={key}
                                />
                              ))}
                            </div>
                            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                              بدترین
                            </div>
                          </div>
                          <form onSubmit={(e) => addRate(e, key)} className="flex gap-2">
                            <Input type="string" name="rate_name" placeholder="افزودن حالت جدید" className="flex-1" />
                            <Button type="submit" color="primary">
                              افزودن
                            </Button>
                          </form>
                        </div>
                      </AccordionItem>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Accordion>
            ) : (
              <div className="grid grid-cols-1 divide-y-2">
                <AnimatePresence>
                  {Object.keys(varriables).map((key, index) => (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{
                        opacity: [1, 0],
                        scale: [1, 1.2, 0],
                        rotate: [0, 5, -5, 0],
                        transition: { duration: 0.4, times: [0, 0.2, 1], ease: "easeOut" },
                      }}
                      transition={{ duration: 0.2 }}
                      className="border px-2 py-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {editingName === key ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="text"
                              defaultValue={key}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleNameEdit(key, e.target.value);
                                } else if (e.key === "Escape") {
                                  setEditingName(null);
                                }
                              }}
                              onBlur={(e) => {
                                if (e.target.value.trim() && e.target.value.trim() !== key) {
                                  handleNameEdit(key, e.target.value);
                                } else {
                                  setEditingName(null);
                                }
                              }}
                              className="w-48"
                            />
                            <Button size="sm" variant="light" onPress={() => setEditingName(null)} className="text-gray-500">
                              لغو
                            </Button>
                          </div>
                        ) : (
                          <>
                            <h3 className="text-lg font-bold">{key}</h3>
                            <Button size="sm" variant="light" onPress={() => setEditingName(key)} className="text-gray-500">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                                />
                              </svg>
                            </Button>
                          </>
                        )}
                      </div>
                      <h3 className="mb-2">
                        وزن معیار را مشخص کنید:{" "}
                        <span className="text-red-600">5 / {weights[index]}</span>
                      </h3>
                      <Slider
                        styles={{
                          track: {
                            width: "100%",
                          },
                          active: {
                            left: 0,
                          },
                        }}
                        axis="x"
                        x={weights[index]}
                        onChange={({ x }) => changeWeights(index, x)}
                        xmax={5}
                        xmin={1}
                        xstep={1}
                      />
                      <div className="my-4 flex flex-row items-center">
                        <input
                          id={`pos${index}`}
                          type="checkbox"
                          checked={!positivities[index]}
                          onChange={(v) => changePositivities(index, v.target.checked)}
                          className="mx-2"
                        />{" "}
                        <label htmlFor={`pos${index}`}>معیار منفی</label>
                        <Popover placement="left" color="primary">
                          <PopoverTrigger>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                              />
                            </svg>
                          </PopoverTrigger>
                          <PopoverContent>
                            <div className="px-1 py-2">
                              <div className="text-small font-bold">
                                معیار منفی معیاری‌ست که تاثیر منفی دارد. مثلا وزن یک لپتاپ که هر چه بیشتر باشد بدتر است.
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <Button
                        color="danger"
                        variant="flat"
                        className="w-full my-4"
                        onClick={() => removeVarriable(key, index)}
                      >
                        حذف معیار
                      </Button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
            <motion.div
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 my-6 hover:border-primary-500 transition-colors cursor-pointer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                const criteriaCount = Object.keys(varriables).length + 1;
                const newName = `معیار شماره‌ی ${criteriaCount}`;
                const defaultRates = problemType === "normal" ? ["خوب", "متوسط", "بد"] : ["زیاد", "متوسط", "کم"];
                setDirtyVarriables(true);
                setVarriables((prev) => ({
                  ...prev,
                  [newName]: defaultRates,
                }));
                setWeights((prev) => [...prev, 2]);
                setPositivities((prev) => [...prev, true]);
                setEditingName(newName);
              }}
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 text-primary-500"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-lg font-medium text-gray-900">افزودن معیار جدید</p>
                  <p className="text-sm text-gray-500 mt-1">برای اضافه کردن معیار جدید کلیک کنید</p>
                </div>
              </div>
            </motion.div>
            </div>
          </div>

          {/* ADMIN/SHARE SECTION */}
          <div className="admin-share-section my-10">
            {(user?.is_admin && isAuthenticated) ? (
              <div className="flex flex-col gap-10">
                <Select
                  isRequired
                  label="انتخاب دسته‌بندی"
                  placeholder="انتخاب یک حالت"
                  className="w-full"
                  defaultSelectedKeys={[category]}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <SelectItem key="private" value="private">
                    خصوصی
                  </SelectItem>
                  <SelectItem key="shared" value="shared">
                    عمومی
                  </SelectItem>
                  <SelectItem key="public" value="public">
                    تصمیم شخصی
                  </SelectItem>
                  <SelectItem key="organization" value="organization">
                    تصمیم سازمانی
                  </SelectItem>
                </Select>

                <div>
                  <label htmlFor="photo">آپلود عکس</label>
                  <input
                    type="file"
                    id="photo"
                    onChange={uploadPhoto}
                    className="w-full rounded-sm py-4 px-6 bordered border-gray-200"
                  />
                </div>

                <Input
                  type="string"
                  label="لینک عکس"
                  placeholder="لینک عکس را از اینجا وارد کنید ( لینک کامل با https )"
                  labelPlacement="inside"
                  value={photoUrl}
                  onChange={(e) => setPhotoUrl(e.target.value)}
                  isRequired={false}
                />
              </div>
            ) : isAuthenticated?(<div className="my-4 flex flex-row items-center">
              <input
                id="save"
                type="checkbox"
                checked={save}
                onChange={(v) => setSave(v.target.checked)}
                className="mx-2"
              />
               <label htmlFor={`save`}>
                  اگر می خواهید تصمیم خود را ذخیره کنید یا با دیگران به
                  اشتراک بگذارید این گزینه را انتخاب کنید
                </label>

            </div>) : (
              <div className="text-center">
                اگر می خواهید تصمیم خود را ذخیره کنید یا با دیگران به
                اشتراک بگذارید لطفا وارد حساب کاربری خود شوید.
              </div>
            )}
            
          </div>


          <Button
            className="w-full mt-6"
            color="secondary"
            variant="solid"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            ثبت معیار‌ها و نام تصمیم
          </Button>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
